import React, { useState, useEffect, useContext, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card } from '../components/ui/Card';
import { Table } from '../components/ui/Table';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Modal } from '../components/ui/Modal';
import { SearchInput } from '../components/ui/SearchInput';
import { Pagination } from '../components/ui/Pagination';
import { DeviceOnboardWizard } from '../components/devices/DeviceOnboardWizard';
import { CameraSetupWizard } from '../components/cameras/CameraSetupWizard';
import { TuringStreamOverlay } from '../components/cameras/TuringStreamOverlay';
import { DeviceDetailModal } from '../components/devices/DeviceDetailModal';
import { SchlagePinManager } from '../components/locks/SchlagePinManager';
import { Device } from '../types';
import { formatDateTime } from '../utils/formatters';
import { getDevices, createDevice } from '../api/devices';
import { apiList } from '../api/apiClient';
import { getProperties } from '../api/properties';
import { getCustomers } from '../api/customers';
import { controlPDKDoor, getPDKDeviceStatus, getPDKLiveDevices } from '../api/pdkApi';
// Seam API imports removed — Seam integration disabled
import { AuthContext } from '../context/AuthContext';
import { BulkActions, useSelection } from '../components/ui/BulkActions';
import { CountBand } from '../components/ui/CountBand';
import { deleteDevice } from '../api/devices';
import { BrandPicker, LockBrand, LOCK_BRANDS } from '../components/locks/BrandPicker';
import { refreshAllCounters } from '../utils/refreshCounters';
import { providerPill, deviceTypeIcon, DeviceStatusIcon, controlButtonFor, controlLabelFor, getIntrlockType, intrlockTypeLabel } from '../components/access/DeviceVisuals';
import { CBWSetupFlow } from '../components/access/CBWSetupFlow';
import { pollAfterAction } from '../utils/postActionPoll';
import { AuthorizedResourceSetupModal } from '../components/integrations/AuthorizedResourceSetupModal';
import { recordFreshness } from '../utils/recordFreshness';

const typeColors: Record<string, string> = {
  lock: 'purple',
  relay: 'yellow',
  smart_lock: 'purple',
  gate: 'indigo',
  acs: 'green',
  camera: 'gray',
  sensor: 'orange',
};

interface DeviceStatus {
  locked?: boolean;
  doorOpen?: boolean;
  online?: boolean;
  alarm?: boolean;
}

export const Devices: React.FC = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const { selected, toggle, toggleAll, clear: clearSelection } = useSelection();
  const [devices, setDevices] = useState<any[]>([]);
  const [properties, setProperties] = useState<any[]>([]);
  const [customers, setCustomers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [searchParams] = useSearchParams();
  // category drives the column set: 'all' (default), 'locks', 'cameras', 'audio', 'sensors', 'lights', 'intrlock'
  const initialCategory = (() => {
    const t = (searchParams.get('type') || searchParams.get('category') || '').toLowerCase();
    if (['lock','smart_lock','gate','acs'].includes(t)) return 'locks';
    if (t === 'camera') return 'cameras';
    if (['audio','speaker','siren','audio_bridge'].includes(t)) return 'audio';
    if (['sensor','dps','motion','door_position'].includes(t)) return 'sensors';
    if (['light','lamp'].includes(t)) return 'lights';
    if (['relay','output'].includes(t)) return 'relays';
    if (['bridge','intercom','panel','kiosk'].includes(t)) return 'intrlock';
    return 'all';
  })();
  const [category, setCategory] = useState<'all' | 'locks' | 'cameras' | 'audio' | 'sensors' | 'lights' | 'relays' | 'intrlock'>(initialCategory as any);
  const [sortKey, setSortKey] = useState<string>('name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [showModal, setShowModal] = useState(false);
  const [showAuthorizedSetup, setShowAuthorizedSetup] = useState(false);
  const [addDeviceStep, setAddDeviceStep] = useState<'choose' | 'lock' | 'camera' | 'cbw'>('choose');
  const [showOnboardWizard, setShowOnboardWizard] = useState(false);
  const [showCameraWizard, setShowCameraWizard] = useState(false);
  const [turingStream, setTuringStream] = useState<{envelopeToken: string; propertyId: string} | null>(null);
  const [selectedDevice, setSelectedDevice] = useState<any | null>(null);
  const [schlagePinDevice, setSchlagePinDevice] = useState<any | null>(null);
  const [lockLoading, setLockLoading] = useState<Record<number, boolean>>({});
  const [deviceStatuses, setDeviceStatuses] = useState<Record<number, DeviceStatus>>({});
  const [cameraProviderStates, setCameraProviderStates] = useState<Record<string, 'online' | 'offline' | 'unknown'>>({});
  const [statusLoading, setStatusLoading] = useState(false);
  const [pdkDeviceIdMap, setPdkDeviceIdMap] = useState<Record<number, string>>({});
  const [acsSystems, setAcsSystems] = useState<any[]>([]);
  const [cloudNodeNames, setCloudNodeNames] = useState<Record<string, string>>({});
  // §28.518.xx — P-OPT-2: ref holds the set of property IDs currently visible
  // on the displayed page. Updated after every render that changes the page.
  // The polling tick reads this ref so it never closes over a stale value.
  const visiblePropertyIdsRef = useRef<Set<any>>(new Set());

  // Keep category in sync with ?type= / ?category= query param so nav links can deep-link.
  useEffect(() => {
    const q = (searchParams.get('type') || searchParams.get('category') || '').toLowerCase();
    if (['lock','smart_lock','gate','acs'].includes(q)) setCategory('locks');
    else if (q === 'camera') setCategory('cameras');
    else if (['audio','speaker','siren','audio_bridge'].includes(q)) setCategory('audio');
    else if (['sensor','dps','motion','door_position'].includes(q)) setCategory('sensors');
    else if (['light','lamp'].includes(q)) setCategory('lights');
    else if (['relay','output'].includes(q)) setCategory('relays');
    else if (['bridge','intercom','panel','kiosk'].includes(q)) setCategory('intrlock');
    else if (q === '') setCategory('all');
  }, [searchParams]);

  // visiblePropertyIds: when provided (idle 60s tick), restrict status polls
  // to those properties only. When undefined (initial load / manual Refresh),
  // fleet-sweep all properties so the status cache is warm for every page.
  const fetchData = async (visiblePropertyIds?: Set<any>) => {
    try {
      setLoading(true);
      const [devData, propData, custData, acsData] = await Promise.all([
        apiList('devices').catch(() => getDevices()),
        getProperties(),
        getCustomers(),
        apiList('access_control_systems').catch(() => []),
      ]);
      const devArr = Array.isArray(devData) ? [...devData] : [];
      const propArr = Array.isArray(propData) ? propData : [];
      const custArr = Array.isArray(custData) ? custData : [];

      // Add Bridge relay channels as virtual devices
      try {
        const bridgeData = await apiList('bridge_devices').catch(() => []);
        const bridgeDevices = (Array.isArray(bridgeData) ? bridgeData : [])
          .filter((b: any) => b.claimed && b.property_id)
          .flatMap((b: any) => {
            const channelNames = b.channel_config_json?.channel_names || {};
            const defaults: Record<number, string> = { 1: 'J2', 2: 'J3', 3: 'J4', 4: 'J5', 5: 'J6', 6: 'J7', 7: 'J8', 8: 'J9' };
            const serial = b.serial_number || b.bridge_device_id || b.id;
            return Array.from({ length: b.channel_count || 0 }, (_, i) => ({
              id: `bridge-${b.id}-ch${i + 1}`,
              name: channelNames[i + 1] || defaults[i + 1] || `Channel ${i + 1}`,
              type: 'relay',
              device_type: 'relay',
              property_id: b.property_id,
              status: 'active',
              acs_provider: 'bridge',
              acs_device_id: `${serial}:ch${i + 1}`,
              connection_status: b.status === 'online' ? 'online' : 'offline',
              manufacturer: 'Intrlock Bridge',
              model: b.name || 'Bridge Relay',
              _bridge: b,
              _channel: i + 1,
            }));
          });
        devArr.push(...bridgeDevices);
      } catch {}

      // Reconcile database rows and connector/Bridge projections by their
      // stable upstream identity. A physical device may be returned through
      // more than one inventory surface with a different Intrlock row id.
      const identityKeys = (d: any): string[] => {
        const rawProvider = String(d.acs_provider || d.provider || d.manufacturer || 'device').toLowerCase().trim();
        const provider = rawProvider === 'prodatakey' ? 'pdk' : rawProvider === 'unifi' ? 'unifi_access' : rawProvider;
        const property = String(d.property_uuid || d.property_id || '');
        const values = [d.integration_device_id, d.acs_device_id, d.pdk_device_id, d.brivo_device_id, d.unifi_device_id, d.vendor_device_id, d.external_id]
          .filter((value: any) => value !== undefined && value !== null && String(value).trim() !== '')
          .map((value: any) => `${provider}:external:${String(value).toLowerCase().trim()}`);
        if (d.serial_number) values.push(`${provider}:serial:${String(d.serial_number).toLowerCase().trim()}`);
        if (d.mac_address || d.mac) values.push(`${provider}:mac:${String(d.mac_address || d.mac).toLowerCase().replace(/[^a-f0-9]/g, '')}`);
        if (provider === 'bridge' && d._channel) values.push(`${provider}:channel:${String(d._bridge?.serial_number || d._bridge?.bridge_device_id || d._bridge?.id)}:${d._channel}`);
        if (!values.length) values.push(`${provider}:row:${property}:${String(d.id)}`);
        return values;
      };
      const dedupedDevices: any[] = [];
      const identityIndex = new Map<string, number>();
      for (const candidate of devArr) {
        const keys = identityKeys(candidate);
        const existingIndex = keys.map(key => identityIndex.get(key)).find(index => index !== undefined);
        if (existingIndex !== undefined) {
          const existing = dedupedDevices[existingIndex];
          // Earlier database rows retain their id; connector projections fill
          // missing live fields without creating another visible card/row.
          dedupedDevices[existingIndex] = { ...candidate, ...existing, _duplicate_sources: [...(existing._duplicate_sources || []), candidate.id] };
          identityKeys(dedupedDevices[existingIndex]).forEach(key => identityIndex.set(key, existingIndex));
        } else {
          const nextIndex = dedupedDevices.length;
          dedupedDevices.push(candidate);
          keys.forEach(key => identityIndex.set(key, nextIndex));
        }
      }

      const acsArr = Array.isArray(acsData) ? acsData : [];
      setAcsSystems(acsArr);

      setDevices(dedupedDevices);
      setProperties(propArr);
      setCustomers(custArr);
      // §28.518.xx — P-OPT-2: pass visiblePropertyIds to restrict status polls
      // to visible page only on idle ticks. Initial load & manual Refresh pass
      // undefined → fleet sweep so all properties are cached immediately.
      fetchDeviceStatuses(dedupedDevices, propArr, visiblePropertyIds);
    } catch (e) {
      console.error('Failed to load devices:', e);
    } finally {
      setLoading(false);
    }
  };

  // §28.518.xx — P-OPT-2: per-property status polling.
  // visiblePropertyIds: when provided, poll ONLY these properties (visible page).
  // When null/undefined (manual Refresh click), poll all properties (fleet sweep).
  const fetchDeviceStatuses = async (devArr: any[], propArr: any[], visiblePropertyIds?: Set<any>) => {
    setStatusLoading(true);
    const statuses: Record<number, DeviceStatus> = {};
    const idMap: Record<number, string> = {};
    const cnNames: Record<string, string> = {};

    // Use v2 /devices/live?property_id= endpoint — server-side aggregates PDK/Brivo/etc.
    // and returns { id, name, online, connection_status, door_open, lock_status, last_seen,
    // cloud_node, cloud_node_connected, can_receive_commands, integration_device_id }
    // See PropertyDetail.tsx for the per-property pattern this mirrors.
    const apiBase = process.env.REACT_APP_API_URL || 'https://api.intrlock.io';
    const token = localStorage.getItem('intrlock_token') || '';
    // Chunk to avoid Lambda concurrency throttling on cold starts. /devices/live
    // calls PDK upstream and is slow (~3s); fanning all properties in parallel
    // spawns 30+ cold Lambda containers and trips throttling → 503/CORS.
    const fetchOne = async (p: any) => {
      try {
        const res = await fetch(`${apiBase}/devices/live?property_id=${p.id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return [];
        const data = await res.json();
        const items = Array.isArray(data) ? data : data.items || [];
        // Preserve the property that produced this response. Device names and
        // vendor IDs are not globally unique and must never leak status across
        // properties.
        return items.map((item: any) => ({ ...item, _live_property_id: String(p.id) }));
      } catch {
        return [];
      }
    };
    // §28.518.xx — P-OPT-2: restrict polling to visible properties only when
    // the caller has passed a Set of property IDs. Fleet sweep is skipped during
    // idle 60s ticks; only the 1-3 properties covering the current page fire.
    const allProps = propArr.filter((p: any) => p.id);
    const queue = visiblePropertyIds
      ? allProps.filter((p: any) => visiblePropertyIds.has(p.id) || visiblePropertyIds.has(p.uuid))
      : allProps;
    const liveResults: any[][] = [];
    const concurrency = 4;
    for (let i = 0; i < queue.length; i += concurrency) {
      const batch = await Promise.all(queue.slice(i, i + concurrency).map(fetchOne));
      liveResults.push(...batch);
    }

    // Build property-scoped lookups across all live responses. A property may
    // be referenced by either its numeric id or UUID, so normalize both aliases
    // to the same scope before matching.
    const propertyScopes = new Map<string, string>();
    for (const property of allProps) {
      const scope = String(property.id);
      propertyScopes.set(scope, scope);
      if (property.uuid) propertyScopes.set(String(property.uuid), scope);
    }
    const scopeFor = (value: unknown) => propertyScopes.get(String(value || '')) || String(value || '');
    const scopedKey = (propertyId: unknown, value: unknown) => `${scopeFor(propertyId)}::${String(value || '').trim().toLowerCase()}`;

    const allLiveByName = new Map<string, any>();
    const allLiveByID = new Map<string, any>();
    for (const liveDevices of liveResults) {
      for (const ld of liveDevices) {
        const propertyId = ld._live_property_id || ld.property_id || ld.property_uuid;
        if (ld.id) allLiveByID.set(scopedKey(propertyId, ld.id), ld);
        if (ld.integration_device_id) allLiveByID.set(scopedKey(propertyId, ld.integration_device_id), ld);
        if (ld.name) allLiveByName.set(scopedKey(propertyId, ld.name), ld);
        if (ld.cloud_node && ld.integration_device_id) {
          cnNames[String(ld.integration_device_id)] = ld.cloud_node;
        }
      }
    }
    setCloudNodeNames(prev => ({ ...prev, ...cnNames }));

    // Match DB devices to live data — prefer id, then integration_device_id, then name
    for (const d of devArr) {
      const propertyId = d.property_id || d.property_uuid;
      const ddId = d.id ? String(d.id) : '';
      const integId = d.integration_device_id ? String(d.integration_device_id) : '';
      const live = (ddId && allLiveByID.get(scopedKey(propertyId, ddId)))
        || (integId && allLiveByID.get(scopedKey(propertyId, integId)))
        || allLiveByName.get(scopedKey(propertyId, d.name || d.device_name));
      if (live) {
        // ONLY set locked / doorOpen if upstream actually told us. Defaulting
        // to false here makes the column read "Unlocked" on every device
        // when the integration call failed (no lock_status in payload),
        // which is the opposite of the truth.
        const lockedKnown = typeof live.locked === 'boolean'
          || typeof live.lock_status === 'string';
        const doorKnown = typeof live.door_open === 'boolean';
        const status: DeviceStatus = {
          online: live.online === true || live.connection_status === 'online',
          alarm: live.prop_alarm_on === true || live.forced_alarm_on === true,
        };
        if (lockedKnown) {
          status.locked = live.lock_status === 'locked' || live.locked === true;
        }
        if (doorKnown) {
          status.doorOpen = live.door_open === true;
        }
        statuses[d.id] = status;
        if (live.integration_device_id) idMap[d.id] = String(live.integration_device_id);
      }
    }
    setPdkDeviceIdMap(prev => ({ ...prev, ...idMap }));

    setDeviceStatuses(statuses);
    setStatusLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  // The generic /devices/live projection does not report current Turing or
  // UniFi Protect camera inventory reliably. Query those providers directly,
  // as the Cameras page does, and treat an unavailable inventory as Unknown
  // instead of turning a last-known PG value into a false Offline verdict.
  useEffect(() => {
    const cameras = devices.filter((d: any) =>
      ['turing', 'unifi_protect'].includes(String(d.acs_provider || '')) &&
      ['camera', 'cam', 'bridge_cam'].includes(String(d.device_type || d.type || '').toLowerCase()));
    if (!cameras.length) return;
    let cancelled = false;
    setCameraProviderStates(Object.fromEntries(cameras.map((d: any) => [String(d.id), 'unknown'])));
    const apiBase = process.env.REACT_APP_API_URL || 'https://api.intrlock.io';
    const token = localStorage.getItem('intrlock_token') || '';
    const headers: HeadersInit = token ? { Authorization: `Bearer ${token}` } : {};
    const normalize = (value: unknown) => String(value || '').trim().toLowerCase();
    void (async () => {
      const providers = [
        { key: 'turing', route: 'turing' },
        { key: 'unifi_protect', route: 'unifi-protect' },
      ];
      const systemGroups = await Promise.all(providers.map(async ({ key, route }) => {
        try {
          const response = await fetch(`${apiBase}/integrations/${route}/systems`, { headers });
          const payload = response.ok ? await response.json() : [];
          return { key, route, systems: Array.isArray(payload) ? payload : [] };
        } catch { return { key, route, systems: [] as any[] }; }
      }));
      const inventories = new Map<string, any[] | null>();
      await Promise.all(systemGroups.flatMap(group => group.systems.map(async (system: any) => {
        const key = `${group.key}:${system.id}`;
        try {
          const response = await fetch(`${apiBase}/integrations/${group.route}/systems/${system.id}/cameras?limit=100`, { headers });
          if (!response.ok) { inventories.set(key, null); return; }
          const payload = await response.json();
          const rows = Array.isArray(payload) ? payload : (payload.cameras || payload.data || []);
          inventories.set(key, Array.isArray(rows) ? rows : null);
        } catch { inventories.set(key, null); }
      })));
      const states: Record<string, 'online' | 'offline' | 'unknown'> = {};
      for (const camera of cameras) {
        const group = systemGroups.find(item => item.key === camera.acs_provider);
        const property = properties.find((p: any) => String(p.id || p.uuid) === String(camera.property_id));
        const customerId = camera.customer_id || property?.customer_id;
        const system = group?.systems.find((s: any) => String(s.id) === String(camera.acs_system_id))
          || group?.systems.find((s: any) => s.entity_type === 'property' && String(s.entity_id) === String(camera.property_id))
          || group?.systems.find((s: any) => s.entity_type === 'customer' && String(s.entity_id) === String(customerId));
        const inventory = system ? inventories.get(`${camera.acs_provider}:${system.id}`) : null;
        const externalId = String(camera.acs_device_id || camera.integration_device_id || '');
        const match = inventory?.find((row: any) =>
          (externalId && String(row.id ?? row.cameraId ?? '') === externalId)
          || (normalize(row.name || row.displayName || row.modelKey) === normalize(camera.name || camera.device_name)));
        const state = normalize(match?.state || match?.connection_status || match?.connectionState);
        states[String(camera.id)] = !match ? 'unknown'
          : (match.online === true || match.isConnected === true || ['running', 'online', 'connected'].includes(state)) ? 'online' : 'offline';
      }
      if (!cancelled) setCameraProviderStates(states);
    })();
    return () => { cancelled = true; };
  }, [devices, properties]);

  // §28.518.xx — P-OPT-2 polling strategy:
  // - Idle 60s tick: poll ONLY visiblePropertyIds (1-3 properties on current page)
  // - Tab focus / visibility restore: fleet-sweep (undefined) so returning users
  //   see fresh state across all properties, not just the visible page slice.
  // connector-api 60s cache (P-OPT-1) makes per-visible-page calls nearly free.
  useEffect(() => {
    let iv: any = null;
    // Idle tick — restrict to currently-visible page properties only
    const idleTick = () => {
      if (!document.hidden) fetchData(visiblePropertyIdsRef.current);
    };
    // Focus/visibility restore — fleet sweep to warm the full cache
    const fullTick = () => { if (!document.hidden) fetchData(); };
    const start = () => { if (!iv) iv = setInterval(idleTick, 60_000); };
    const stop = () => { if (iv) { clearInterval(iv); iv = null; } };
    const onVisibility = () => {
      if (document.hidden) { stop(); }
      else { fullTick(); start(); }  // fleet-sweep on restore, then resume idle ticks
    };
    const onFocus = () => { fullTick(); };
    start();
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      stop();
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  const getPropertyName = (propId: number) => {
    const p = properties.find((p: any) => p.id === propId);
    return p?.name || `Property #${propId}`;
  };

  const getPropertySystemId = (propId: number) => {
    const p = properties.find((p: any) => p.id === propId);
    return p?.pdk_system_id || '';
  };

  const getDeviceProvider = (d: any) => {
    if (d.acs_provider === 'bridge') return 'Intrlock Bridge';
    if (d.acs_provider === 'pdk') return 'PDK';
    if (d.acs_provider === 'schlage') return 'Schlage';
    const brand = LOCK_BRANDS.find(b => b.key === d.acs_provider);
    if (brand) return brand.name;
    if (d.acs_provider === 'seam') return d.manufacturer || 'Smart Lock';
    if (d.pdk_device_id || d.pdk_system_id) return 'PDK';
    return d.acs_provider || d.manufacturer || '—';
  };

  const getDeviceSystemName = (d: any) => {
    const sysId = d.pdk_system_id || getPropertySystemId(d.property_id);
    if (!sysId) {
      if (d.acs_provider === 'bridge' && d._bridge) return d._bridge.name || 'Bridge';
      return '—';
    }
    const sys = acsSystems.find((s: any) => s.pdk_system_id === sysId || s.system_id === sysId);
    const sysName = sys?.name || sys?.system_name || sysId.slice(0, 8);
    const cnId = d.pdk_cloud_node_id;
    const cnName = cnId ? (cloudNodeNames[cnId] || cnId.slice(0, 8)) : '';
    return cnName ? `${sysName} / ${cnName}` : sysName;
  };

  const getDeviceOnline = (d: any) => {
    const live = deviceStatuses[d.id];
    if (live?.online !== undefined) return live.online;
    if (typeof d.online === 'boolean') return d.online;
    const connection = String(d.connection_status || d.online_status || '').toLowerCase();
    return connection === 'online' || connection === 'connected';
  };
  const getDisplayConnectivity = (d: any): 'online' | 'offline' | 'unknown' => {
    if (['turing', 'unifi_protect'].includes(String(d.acs_provider || '')) &&
        ['camera', 'cam', 'bridge_cam'].includes(String(d.device_type || d.type || '').toLowerCase())) {
      return cameraProviderStates[String(d.id)] || 'unknown';
    }
    return getDeviceOnline(d) ? 'online' : 'offline';
  };

  // Role-based filtering
  // Discovered vendor inventory stays in integration/system management until
  // it is explicitly assigned to a property in the visible portfolio.
  let filtered = devices.filter((d:any) => {
    const propertyId=String(d.property_id||d.property_uuid||'');
    return !!propertyId && properties.some((p:any)=>String(p.uuid||p.id)===propertyId);
  });
  if (user?.role === 'customer') {
    const custProps = properties.filter((p: any) => String(p.customer_id) === String(user.customer_id)).map((p: any) => String(p.uuid || p.id));
    filtered = filtered.filter((d: any) => custProps.includes(String(d.property_id)));
  } else if (user?.role === 'integrator' || user?.role === 'dealer') {
    const intCusts = customers.filter((c: any) => String(c.integrator_id) === String(user.integrator_id)).map((c: any) => String(c.uuid || c.id));
    const intProps = properties.filter((p: any) => intCusts.includes(String(p.customer_id))).map((p: any) => String(p.uuid || p.id));
    filtered = filtered.filter((d: any) => intProps.includes(d.property_id));
  } else if (user?.role === 'property_manager' && user?.property_ids) {
    filtered = filtered.filter((d: any) => user.property_ids!.includes(d.property_id));
  }

  // Search
  if (search) {
    const s = search.toLowerCase();
    filtered = filtered.filter((d: any) =>
      (d.name || '').toLowerCase().includes(s) ||
      (d.model || '').toLowerCase().includes(s) ||
      (d.serial_number || '').toLowerCase().includes(s) ||
      getPropertyName(d.property_id).toLowerCase().includes(s)
    );
  }

  // Category classifier — determines which device belongs in which tab.
  // Locks ⊇ readers: PDK calls the unit a `primaryReader` / `auxiliaryReader`
  // but to a PM it's a lock — same surface, same control flow. Same for
  // door / gate / smart_lock variants.
  // §28.518.27 — classify off the shared getIntrlockType helper. Intrlock
  // bridge / panel / kiosk stays under its own bucket via provider/mfg.
  const classify = (d: any): 'locks' | 'cameras' | 'audio' | 'sensors' | 'lights' | 'relays' | 'intrlock' | 'other' => {
    const prov = String(d.acs_provider || '').toLowerCase();
    const mfg  = String(d.manufacturer || '').toLowerCase();
    const t    = String(d.type || d.device_type || '').toLowerCase();
    // Intrlock hardware bucket wins first (bridge/panel/kiosk regardless of canonical type).
    if (prov === 'bridge' || ['intercom','panel','kiosk','bridge'].includes(t) || mfg.startsWith('intrlock')) return 'intrlock';
    switch (getIntrlockType(d)) {
      case 'Lock':         return 'locks';
      case 'Relay':        return 'relays';
      case 'Camera':       return 'cameras';
      case 'Siren':
      case 'Speaker':
      case 'Audio Bridge': return 'audio';
      case 'Sensor':       return 'sensors';
      case 'Light':        return 'lights';
      default: break;
    }
    if (prov && ['pdk','brivo','seam'].includes(prov)) return 'locks';
    return 'other';
  };
  if (category !== 'all') {
    filtered = filtered.filter((d: any) => classify(d) === category);
  }

  // Sort helper — reach into common nested paths so the header value maps to a sort key.
  const sortValueFor = (colKey: string, d: any): any => {
    switch (colKey) {
      case 'name': return (d.name || d.device_name || '').toString().toLowerCase();
      case 'provider': return (d.acs_provider || getDeviceProvider(d) || '').toString().toLowerCase();
      case 'property': {
        const p = properties.find((pr: any) => pr.id === d.property_id || pr.uuid === d.property_id);
        return (p?.name || '').toLowerCase();
      }
      case 'customer': {
        const p = properties.find((pr: any) => pr.id === d.property_id || pr.uuid === d.property_id);
        const c = customers.find((c: any) => c.id === p?.customer_id || c.uuid === p?.customer_id);
        return (c?.name || c?.company_name || '').toLowerCase();
      }
      case 'online': return { online: 0, offline: 1, unknown: 2 }[getDisplayConnectivity(d)];
      case 'locked': {
        const live = deviceStatuses[d.id];
        if (live?.locked === undefined) return 2;
        return live.locked ? 0 : 1;
      }
      case 'position': {
        const live = deviceStatuses[d.id];
        if (live?.doorOpen === undefined) return 2;
        return live.doorOpen ? 1 : 0;
      }
      case 'acs_system': {
        const sysId = d.pdk_system_id || getPropertySystemId(d.property_id);
        const sys = sysId ? acsSystems.find((s: any) => s.pdk_system_id === sysId || s.system_id === sysId) : null;
        return (sys?.name || (d.acs_provider === 'bridge' && d._bridge?.name) || '').toLowerCase();
      }
      case 'created_at': return d.created_at ? new Date(d.created_at).getTime() : 0;
      case 'type': return intrlockTypeLabel(d).toLowerCase();
      case 'status': return (d.status || '').toLowerCase();
      case 'data_type': return (d.data_type || d._cellular?.data_type || '').toLowerCase();
      case 'cellular_usage': return d.cellular_usage_mb ?? d._cellular?.current_period_data_used_mb ?? -1;
      default: return d[colKey];
    }
  };

  // Apply sort BEFORE pagination.
  filtered = [...filtered].sort((a, b) => {
    const av = sortValueFor(sortKey, a);
    const bv = sortValueFor(sortKey, b);
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    if (av < bv) return sortDir === 'asc' ? -1 : 1;
    if (av > bv) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  // Pagination
  const ITEMS_PER_PAGE = 20;
  const [page, setPage] = useState(1);
  const totalPages = Math.max(1, Math.ceil(filtered.length / ITEMS_PER_PAGE));
  const safePage = Math.min(page, totalPages);
  const startIdx = (safePage - 1) * ITEMS_PER_PAGE;
  const paginatedFiltered = filtered.slice(startIdx, startIdx + ITEMS_PER_PAGE);
  const endIdx = Math.min(startIdx + ITEMS_PER_PAGE, filtered.length);

  // §28.518.xx — P-OPT-2: keep the ref current so the idle polling tick
  // always polls the right set of properties for the visible page.
  // Using useEffect with paginatedFiltered as dep would risk stale closure;
  // we update the ref imperatively instead (no re-render triggered).
  const visiblePropIds = new Set(paginatedFiltered.map((d: any) => d.property_id).filter(Boolean));
  visiblePropertyIdsRef.current = visiblePropIds;

  const handleLockToggle = async (device: any) => {
    const devId = device.id;
    const status = deviceStatuses[devId];
    const currentlyLocked = status?.locked !== undefined ? status.locked : true;

    setLockLoading(prev => ({ ...prev, [devId]: true }));
    try {
      // The browser sends only Intrlock's opaque device UUID. The access API
      // verifies property scope, resolves vendor identifiers server-side, and
      // invokes the connector with encrypted credentials.
      const opaqueAction = currentlyLocked ? 'unlock' : 'lock';
      const apiBase = process.env.REACT_APP_API_URL || 'https://api.intrlock.io';
      const tk = localStorage.getItem('intrlock_token') || '';
      const r = await fetch(`${apiBase}/devices/${encodeURIComponent(String(devId))}/actions/${opaqueAction}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tk}` },
        body: JSON.stringify({ duration_ms: 5000 }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok || data.success === false || (opaqueAction === 'unlock' && data.door_unlocked === false)) {
        throw new Error(data?.error || data?.detail || `${opaqueAction} failed (${r.status})`);
      }
      setDeviceStatuses(prev => ({
        ...prev,
        [devId]: { ...prev[devId], locked: !currentlyLocked },
      }));
      // §28.518.80 — P-OPT-2 event trigger: after a successful action fires,
      // poll ONLY this device's property 3× at 1.5s intervals via shared
      // pollAfterAction helper. Fire-and-forget (no await) so button UI
      // doesn't block on the 4.5s poll window. Per-property scoping preserved.
      const propIdSet = new Set([device.property_id].filter(Boolean));
      if (propIdSet.size > 0) {
        const devArrSnap = devices;
        const propArrSnap = properties;
        pollAfterAction(
          () => { fetchDeviceStatuses(devArrSnap, propArrSnap, propIdSet); return Promise.resolve(); },
          String(devId),
          opaqueAction,
        );
      }
    } catch (e) {
      console.error('Lock control failed:', e);
    } finally {
      setLockLoading(prev => ({ ...prev, [devId]: false }));
    }
  };

  const isControllableDevice = (d: any) => {
    // §28.518.36 — any device whose Intrlock canonical type has an action
    // is controllable via the opaque POST /devices/:id/actions/:action
    // endpoint. Locks/sirens/speakers/audio-bridges/lights/relays all route
    // to try_open on PDK (which fires the relay — same physical mechanism
    // whether the load is a door lock, siren, or light).
    const it = getIntrlockType(d);
    if (['Lock','Siren','Speaker','Audio Bridge','Light','Relay'].includes(it)) return true;
    const rawCapabilities = Array.isArray(d.raw?.capabilities) ? d.raw.capabilities.map(String) : [];
    if (d.source === 'acs' && d.raw?.location_id && rawCapabilities.some((value: string) => /(^|_)is_reader$/.test(value))) return true;
    // Fallback: legacy capability check.
    if (Array.isArray(d.capabilities) && d.capabilities.includes('unlock')) return true;
    if (d.acs_provider === 'bridge' && d.acs_device_id) return true;
    if (d.acs_provider === 'brivo' && d.acs_device_id) return true;
    if (d.acs_provider === 'schlage' && d.acs_device_id && d.acs_system_id) return true;
    if (pdkDeviceIdMap[d.id]) return true;
    return false;
  };

  const getBatteryColor = (pct: number) => {
    if (pct > 60) return 'text-green-600';
    if (pct > 20) return 'text-yellow-600';
    return 'text-red-600';
  };

  // Summary stats — connectivity is authoritative only when explicitly
  // reported. Lifecycle state (for example `active`) and a configured stream
  // URL do not prove that a device is currently online.
  const totalDevices = filtered.length;
  const onlineCount = filtered.filter((d: any) => getDisplayConnectivity(d) === 'online').length;
  const offlineCount = filtered.filter((d: any) => getDisplayConnectivity(d) === 'offline').length;
  const unknownCount = totalDevices - onlineCount - offlineCount;

  // Build column set once — shared by the Table and the Sort-by dropdown.
  const activeColumns = useMemo(() => {
    const nameCol = { key: 'name', header: 'Device Name', sticky: 'primary' as const, render: (d: any) => <button type="button" onClick={(e) => { e.stopPropagation(); setSelectedDevice(d); }} className="font-semibold text-brand-700 hover:underline">{d.name || d.device_name}</button> };
    const providerCol = { key: 'provider', header: 'Device Provider', render: (d: any) => providerPill({ acs_provider: d.acs_provider || getDeviceProvider(d) }) };
    const propertyCol = { key: 'property', header: 'Intrlock Property', render: (d: any) => {
      const p = properties.find((pr: any) => pr.id === d.property_id || pr.uuid === d.property_id);
      return <span className="text-sm text-gray-700">{p?.name || '—'}</span>;
    }};
    const customerCol = { key: 'customer', header: 'Intrlock Customer', render: (d: any) => {
      const p = properties.find((pr: any) => pr.id === d.property_id || pr.uuid === d.property_id);
      const c = customers.find((c: any) => c.id === p?.customer_id || c.uuid === p?.customer_id);
      return <span className="text-sm text-gray-700">{c?.name || c?.company_name || '—'}</span>;
    }};
    const onlineCol = { key: 'online', header: 'Online Status', render: (d: any) => {
      const state = getDisplayConnectivity(d);
      return state === 'unknown' ? <span className="prototype-status inactive" title="Live provider state unavailable">Unknown</span> : (
        <div className="flex items-center gap-1.5">
          <DeviceStatusIcon online={state === 'online'} />
          <span className="text-sm">{state === 'online' ? 'Online' : 'Offline'}</span>
        </div>
      );
    }};
    // §28.518.37 — contextual Status column. Header switches based on
    // active category ("Lock Status" / "Camera Status" / …); render dispatches
    // by Intrlock canonical type. Reads from d.state.* (opaque API §28.518.30)
    // first, falls back to deviceStatuses[d.id] (legacy live-fetch pipeline).
    const statusHeaderFor = (cat: string) => ({
      locks:'Lock Status', cameras:'Camera Status', audio:'Audio Status',
      sensors:'Sensor Status', lights:'Light Status', relays:'Relay Status', intrlock:'Device Status',
    } as Record<string,string>)[cat] || 'Status';
    const lockCol = { key: 'status', header: statusHeaderFor(category), render: (d: any) => {
      const it = getIntrlockType(d);
      const live = deviceStatuses[d.id] || {};
      const s = (d.state && typeof d.state === 'object') ? d.state : {};
      const locked = (s.locked ?? live.locked);
      const doorOpen = (s.dpsOpen ?? live.doorOpen);
      const relayOpen = s.relayOpen;
      const online = getDeviceOnline(d);
      if (!online) return <span className="text-sm text-gray-500">Offline</span>;
      if (it === 'Lock') {
        if (locked === undefined) return <span className="text-gray-400">—</span>;
        return <span className={`text-sm font-medium ${locked ? 'text-green-700' : 'text-red-600'}`}>{locked ? 'Locked' : 'Unlocked'}</span>;
      }
      if (it === 'Camera') return <span className="text-sm text-green-700 font-medium">Streaming</span>;
      if (it === 'Siren' || it === 'Speaker' || it === 'Audio Bridge') {
        return <span className={`text-sm font-medium ${relayOpen ? 'text-orange-600' : 'text-gray-600'}`}>{relayOpen ? 'Active' : 'Idle'}</span>;
      }
      if (it === 'Light') {
        return <span className={`text-sm font-medium ${relayOpen ? 'text-yellow-600' : 'text-gray-600'}`}>{relayOpen ? 'On' : 'Off'}</span>;
      }
      if (it === 'Sensor') {
        return <span className={`text-sm font-medium ${doorOpen ? 'text-orange-600' : 'text-green-700'}`}>{doorOpen ? 'Tripped' : 'Clear'}</span>;
      }
      if (it === 'Relay') return <span className={`text-sm font-medium ${relayOpen ? 'text-brand-600' : 'text-gray-600'}`}>{relayOpen ? 'Energized' : 'Idle'}</span>;
      return <span className="text-sm text-gray-500">{d.status || 'Active'}</span>;
    }};
    const positionCol = { key: 'position', header: 'Position Status', render: (d: any) => {
      const live = deviceStatuses[d.id];
      if (live?.doorOpen === undefined) return <span className="text-gray-400">—</span>;
      return <span className={`text-sm ${live.doorOpen ? 'text-orange-600' : 'text-green-600'}`}>{live.doorOpen ? 'Open' : 'Closed'}</span>;
    }};
    const acsSystemCol = { key: 'acs_system', header: 'ACS Device System Name', render: (d: any) => {
      // §28.518.34 — opaque model: prefer the API's inlined acs_system_name
      // (returned by GET /devices), then look up by opaque acs_system_id
      // in the acsSystems list, then fall back to legacy pdk_system_id
      // lookup for pre-per-CN rows, then bridge name.
      const direct = d.acs_system_name || d.acs_system?.name;
      if (direct) return <span className="text-sm text-gray-700">{direct}</span>;
      const byOpaqueId = d.acs_system_id
        ? acsSystems.find((s: any) => s.id === d.acs_system_id || s.uuid === d.acs_system_id)
        : null;
      const legacySysId = d.pdk_system_id || getPropertySystemId(d.property_id);
      const byLegacy = legacySysId
        ? acsSystems.find((s: any) => s.pdk_system_id === legacySysId || s.system_id === legacySysId)
        : null;
      const name = byOpaqueId?.name || byLegacy?.name || (d.acs_provider === 'bridge' && d._bridge?.name) || '—';
      return <span className="text-sm text-gray-700">{name}</span>;
    }};
    const createdCol = { key: 'created_at', header: 'Created At', render: (d: any) => {
      const ts = d.created_at;
      return ts ? <span className="text-sm text-gray-500">{formatDateTime(ts)}</span> : <span className="text-gray-400">—</span>;
    }};
    const typeCol = { key: 'type', header: 'Intrlock Device Type', render: (d: any) => {
      const state = getDisplayConnectivity(d);
      return (
        <div className="flex items-center gap-2">
          {deviceTypeIcon(d, state === 'offline')}
          <span className="text-sm text-gray-700">{intrlockTypeLabel(d)}</span>
        </div>
      );
    }};
    const statusCol = { key: 'status', header: 'Status', render: (d: any) => (
      <Badge color={d.status === 'active' ? 'green' : d.status === 'inactive' ? 'gray' : 'gray'}>{d.status || '—'}</Badge>
    )};
    const dataTypeCol = { key: 'data_type', header: 'Data Type', render: (d: any) => (
      <span className="text-sm text-gray-700">{d.data_type || d._cellular?.data_type || (d.acs_provider === 'bridge' ? 'Wi-Fi/LAN' : '—')}</span>
    )};
    const cellularCol = { key: 'cellular_usage', header: 'Cellular Usage', render: (d: any) => {
      const used = d.cellular_usage_mb ?? d._cellular?.current_period_data_used_mb;
      if (used == null) return <span className="text-gray-400">—</span>;
      return <span className="text-sm text-gray-700">{used} MB</span>;
    }};
    const actionsCol = { key: 'grant_access', header: 'Device Control', render: (d: any) => {
      const isBridgeVirtual = typeof d.id === 'string' && String(d.id).startsWith('bridge-');
      // §28.518.36 — every device row shows a Device Control button.
      // Actionable types (lock/siren/speaker/audio_bridge/light/relay) fire
      // the opaque unlock/toggle action; non-actionable types (camera/sensor)
      // open the DeviceDetailModal to view live state.
      const label = controlLabelFor(d);
      const isViewOnly = label === 'View Feed' || label === 'View State';
      const onClickBtn = () => {
        if (isViewOnly) setSelectedDevice(d);
        else handleLockToggle(d);
      };
      const controllable = isControllableDevice(d) || isViewOnly;
      const isTuringCam = d.acs_provider === 'turing' && (d.device_type === 'camera' || d.type === 'camera');
      return (
        <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
          {isTuringCam && d.envelope_token && d.property_id && (
            <button
              onClick={() => setTuringStream({ envelopeToken: d.envelope_token, propertyId: d.property_id })}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold inline-flex items-center gap-1 bg-brand-600 text-white hover:bg-brand-700 cursor-pointer"
              style={{ minWidth: 150, justifyContent: 'center' }}
            >
              View Stream
            </button>
          )}
          {label && !isTuringCam && controlButtonFor(
            d,
            onClickBtn,
            { disabled: !isViewOnly && (!controllable || !getDeviceOnline(d)), loading: !!lockLoading[d.id] }
          )}
          {d.acs_provider === 'schlage' && d.acs_system_id && d.acs_device_id && (
            <button
              onClick={() => setSchlagePinDevice(d)}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
              style={{ minWidth: 150, justifyContent: 'center' }}
            >
              PIN Codes
            </button>
          )}
          {!isBridgeVirtual && (
            <button
              onClick={async () => {
                if (!window.confirm(`Delete "${d.name || d.device_name}"? This cannot be undone.`)) return;
                try {
                  await deleteDevice(d.id);
                  refreshAllCounters();
                  fetchData();
                } catch (e) {
                  console.error('Failed to delete device:', e);
                  alert('Failed to delete device');
                }
              }}
              className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
              title="Delete device"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          )}
        </div>
      );
    }};
    const openCol = { key: '_actions', header: '', sticky: 'actions' as const, render: (d: any) => (
      <button type="button" onClick={(e) => { e.stopPropagation(); setSelectedDevice(d); }} className="row-open">Open <span aria-hidden="true">→</span></button>
    )};
    if (category === 'locks')    return [nameCol, providerCol, acsSystemCol, propertyCol, onlineCol, createdCol, actionsCol, openCol];
    if (category === 'cameras')  return [nameCol, providerCol, propertyCol, onlineCol, createdCol, actionsCol, openCol];
    if (category === 'audio')    return [nameCol, providerCol, acsSystemCol, propertyCol, onlineCol, actionsCol, openCol];
    if (category === 'sensors')  return [nameCol, providerCol, propertyCol, onlineCol, createdCol, openCol];
    if (category === 'lights')   return [nameCol, providerCol, propertyCol, onlineCol, actionsCol, openCol];
    if (category === 'relays')   return [nameCol, providerCol, acsSystemCol, propertyCol, onlineCol, actionsCol, openCol];
    if (category === 'intrlock') return [nameCol, customerCol, propertyCol, typeCol, onlineCol, dataTypeCol, cellularCol, createdCol, actionsCol, openCol];
    return [nameCol, providerCol, typeCol, acsSystemCol, propertyCol, onlineCol, actionsCol, openCol];
  }, [category, properties, customers, acsSystems, deviceStatuses, cameraProviderStates, lockLoading, cloudNodeNames, pdkDeviceIdMap]);

  return (
    <div className="shadow-list-page has-prototype-toolbar devices-list-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Devices</h1>
          <p className="text-gray-500 mt-1">Monitor and manage all devices</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setShowAuthorizedSetup(true)}>
            <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Device
          </Button>
        </div>
      </div>
      <CountBand matches={filtered.length} total={devices.length} detail="Integration devices matching filters in the current accessible portfolio" />
      <div className="prototype-toolbar devices-toolbar">
        <SearchInput value={search} onChange={(v) => { setSearch(v); setPage(1); }} placeholder="Search devices..." />
        <label><small>Type</small><select value={category} onChange={e => { setCategory(e.target.value as any); setPage(1); }}><option value="all">All devices</option><option value="locks">Locks</option><option value="cameras">Cameras</option><option value="audio">Audio</option><option value="sensors">Sensors</option><option value="lights">Lights</option><option value="relays">Relays</option><option value="intrlock">Intrlock devices</option></select></label>
        <label><small>Sort</small><select value={sortKey} onChange={e => setSortKey(e.target.value)}>{activeColumns.filter((c: any) => c.key !== 'grant_access' && c.key !== '_actions' && typeof c.header === 'string' && c.header).map((c: any) => <option key={c.key} value={c.key}>{c.header}</option>)}</select></label>
        <button className="device-sort-direction" onClick={() => setSortDir(d => d === 'asc' ? 'desc' : 'asc')} title={sortDir === 'asc' ? 'Ascending' : 'Descending'}>{sortDir === 'asc' ? '↑ Asc' : '↓ Desc'}</button>
        <div className="device-status-summary"><span><b>{statusLoading ? '…' : onlineCount}</b> Online</span><span><b>{statusLoading ? '…' : offlineCount}</b> Offline</span>{unknownCount > 0 && <span><b>{unknownCount}</b> Unknown</span>}</div>
        <Button onClick={() => setShowAuthorizedSetup(true)}>Add Device</Button>
      </div>

      {/* Mobile: cards (no table on small screens). */}
      <div className="md:hidden mb-4 space-y-3">
        <div className="grid grid-cols-1 gap-2">
          {loading ? (
            <div className="bg-white rounded-xl border border-gray-200 p-6 text-center text-sm text-gray-400">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-200 p-6 text-center text-sm text-gray-400">No devices found.</div>
          ) : paginatedFiltered.map((d: any) => {
            const status = getDisplayConnectivity(d);
            const sc = status === 'online' ? 'bg-green-100 text-green-700' : status === 'offline' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700';
            const property = properties.find((p: any) => String(p.id) === String(d.property_id) || String(p.uuid) === String(d.property_id));
            const system = d.acs_system_name || d.acs_system?.name || acsSystems.find((s: any) => String(s.id) === String(d.acs_system_id) || String(s.uuid) === String(d.acs_system_id))?.name || (d.acs_provider === 'bridge' ? d._bridge?.name : '') || '—';
            const freshness = recordFreshness(d);
            return (
              <article key={d.id} onClick={() => setSelectedDevice(d)} className="prototype-mobile-card device-mobile-card cursor-pointer">
                <header>
                  <div className="device-mobile-title">{deviceTypeIcon(d, status === 'offline')}<strong>{d.name || d.device_name || `Device #${d.id}`}</strong></div>
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${sc}`}>{status}</span>
                </header>
                <dl className="device-mobile-details">
                  <div><dt>Provider</dt><dd>{providerPill({ acs_provider: d.acs_provider || getDeviceProvider(d) })}</dd></div>
                  <div><dt>Type</dt><dd>{intrlockTypeLabel(d)}</dd></div>
                  <div className="wide"><dt>Property</dt><dd>{property?.name || 'Unassigned'}</dd></div>
                  <div className="wide"><dt>System</dt><dd>{system}</dd></div>
                  <div className="wide"><dt>{freshness?.label || 'Freshness'}</dt><dd>{freshness ? formatDateTime(freshness.value) : 'Not reported'}</dd></div>
                </dl>
                <div className="mobile-card-actions">
                  <button onClick={(e) => { e.stopPropagation(); setSelectedDevice(d); }} className="row-open">Open <span aria-hidden="true">→</span></button>
                </div>
              </article>
            );
          })}
        </div>
        {!loading && filtered.length > 0 && <div className="mobile-pagination-card"><Pagination page={safePage} totalPages={totalPages} totalItems={filtered.length} startIndex={startIdx} endIndex={endIdx} onPageChange={setPage} /></div>}
      </div>

      {/* Desktop: table */}
      <Card padding={false} className="hidden md:block">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Loading devices...</div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-gray-400">No devices found.</div>
        ) : (
          <Table
            columns={activeColumns as any}
            data={paginatedFiltered}
            onRowClick={(d: any) => setSelectedDevice(d)}
          />
        )}
        <Pagination page={safePage} totalPages={totalPages} totalItems={filtered.length} startIndex={startIdx} endIndex={endIdx} onPageChange={setPage} />
      </Card>

      <Modal open={showModal} onClose={() => { setShowModal(false); setAddDeviceStep('choose'); }} title="Add Device">
        {addDeviceStep === 'choose' && (
          <div className="space-y-3">
            <p className="text-sm text-gray-500 mb-4">What type of device do you want to add?</p>
            <button onClick={() => setAddDeviceStep('lock')} className="w-full text-left p-4 border border-gray-200 rounded-xl hover:border-brand-500 hover:bg-brand-50 transition-colors">
              <p className="font-semibold text-gray-900">🔒 Lock</p>
              <p className="text-sm text-gray-500 mt-1">Add a lock from one of your access control systems</p>
            </button>
            <button
              onClick={() => { setShowModal(false); setAddDeviceStep('choose'); setShowCameraWizard(true); }}
              className="w-full text-left p-4 border border-gray-200 rounded-xl hover:border-brand-500 hover:bg-brand-50 transition-colors"
            >
              <p className="font-semibold text-gray-900">📷 Camera</p>
              <p className="text-sm text-gray-500 mt-1">Auto-discover ONVIF, add by RTSP, connect an NVR, or pair a Bridge Camera</p>
            </button>
            <button
              onClick={() => { setShowModal(false); setAddDeviceStep('choose'); setShowOnboardWizard(true); }}
              className="w-full text-left p-4 border border-gray-200 rounded-xl hover:border-brand-500 hover:bg-brand-50 transition-colors"
            >
              <p className="font-semibold text-gray-900">📡 Intrlock Device</p>
              <p className="text-sm text-gray-500 mt-1">Pair an Intrlock Bridge, panel, or kiosk by QR / MAC</p>
            </button>
            <button
              onClick={() => setAddDeviceStep('cbw')}
              className="w-full text-left p-4 border border-gray-200 rounded-xl hover:border-brand-500 hover:bg-brand-50 transition-colors"
            >
              <p className="font-semibold text-gray-900">⚡ Control By Web</p>
              <p className="text-sm text-gray-500 mt-1">Provision a CBW modular relay or IP I/O device</p>
            </button>
          </div>
        )}
        {addDeviceStep === 'lock' && (() => {
          const canSetupNewAcs = !!user && (user.role === 'super_admin' || user.role === 'admin' || user.role === 'integrator' || user.role === 'dealer' || user.role === 'customer');
          const sysList = (acsSystems || []) as any[];
          return (
            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-600">Pick an access control system. The system detail page lists every available {`<system>`} device — click <strong>Assign property</strong> next to a device to add it as an Intrlock lock.</p>
              </div>
              {sysList.length === 0 ? (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                  No access control systems are connected to your account yet.
                </div>
              ) : (
                <div className="space-y-2">
                  {sysList.map((sys: any) => {
                    const cns = Array.isArray(sys.cloud_nodes) ? sys.cloud_nodes : [];
                    const available = cns.reduce((sum: number, cn: any) => sum + Number(cn.available_device_count || 0), 0)
                                    - cns.reduce((sum: number, cn: any) => sum + Number(cn.intrlock_device_count || 0), 0);
                    const customer = customers.find((c: any) => c.id === sys.customer_id || c.uuid === sys.customer_id);
                    const custName = customer?.name || customer?.company_name;
                    return (
                      <button
                        key={sys.id}
                        onClick={() => { setShowModal(false); setAddDeviceStep('choose'); navigate(`/access-control/${sys.id}?tab=devices`); }}
                        className="w-full text-left p-3 border border-gray-200 rounded-lg hover:border-brand-500 hover:bg-brand-50 transition-colors"
                      >
                        <div className="flex items-center justify-between gap-3">
                          <div>
                            <p className="font-medium text-gray-900">{custName || sys.name || sys.system_id || 'ACS system'}</p>
                            <p className="text-xs text-gray-500">
                              {(sys.provider || 'pdk').toUpperCase()} · {cns.length} site{cns.length === 1 ? '' : 's'}
                            </p>
                          </div>
                          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${available > 0 ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                            {Math.max(available, 0)} available
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
              <div className="flex gap-3 justify-between pt-2 border-t border-gray-100">
                <Button variant="secondary" onClick={() => setAddDeviceStep('choose')}>Back</Button>
                {canSetupNewAcs && (
                  <Button onClick={() => { setShowModal(false); setAddDeviceStep('choose'); navigate('/access-control'); }}>
                    Set up a new ACS system
                  </Button>
                )}
              </div>
            </div>
          );
        })()}
        {addDeviceStep === 'cbw' && (
          <CBWSetupFlow
            entryPoint="devices"
            customerId={String(user?.customer_id || '')}
            properties={properties}
            onSuccess={() => { refreshAllCounters(); setShowModal(false); setAddDeviceStep('choose'); fetchData(); }}
            onCancel={() => setAddDeviceStep('choose')}
          />
        )}
      </Modal>
      <AuthorizedResourceSetupModal open={showAuthorizedSetup} mode="device" customers={customers} properties={properties} onClose={() => setShowAuthorizedSetup(false)} />
      <BulkActions selectedIds={selected} onClearSelection={clearSelection} onDelete={async (ids) => { await Promise.all(ids.map(id => deleteDevice(id))); fetchData(); }} entityName="devices" />
      <DeviceOnboardWizard open={showOnboardWizard} onClose={() => setShowOnboardWizard(false)} properties={properties} onDeviceAdded={fetchData} />
      <CameraSetupWizard open={showCameraWizard} onClose={() => setShowCameraWizard(false)} properties={properties} onCameraAdded={fetchData} />
      {turingStream && (
        <TuringStreamOverlay
          envelopeToken={turingStream.envelopeToken}
          propertyId={turingStream.propertyId}
          onClose={() => setTuringStream(null)}
        />
      )}
      <DeviceDetailModal open={!!selectedDevice} onClose={() => setSelectedDevice(null)} device={selectedDevice} onDeviceUpdated={() => fetchData()} />
      {schlagePinDevice && (
        <SchlagePinManager
          open={!!schlagePinDevice}
          onClose={() => setSchlagePinDevice(null)}
          device={schlagePinDevice}
        />
      )}
    </div>
  );
};
